// Per-Block preview screen — single-Block visual fidelity surface. Per
// the P-037 scope expansion (Banks redirect): isolate each Block in its
// own document envelope with editable options/inputs + viewport toggle,
// so brand-fidelity verification happens block-by-block before cumulative
// kitchen-sink testing.
//
// **Synthetic Email envelope.** Wraps the single Block in an Email
// structure that flows through the same `renderEmail()` pipeline as
// real chapter sends — preserves the document shell + per-Block render
// context. The envelope id/templateId/templateName are stable strings
// so memoization holds across Form edits.
//
// **Two Forms.** One for `def.optionsSchema`, one for `def.inputsSchema`.
// The Form generator (P-025+) auto-builds editor fields from each Zod
// schema; discriminated-union options (e.g. events-list.headerStyle) only
// surface the last union arm in the auto-form per the existing Form
// last-arm convention.
//
// **Sample data.** Six Blocks (day-chairs, member-list, event-card-group,
// events-list, other-notable-events, chapter-sponsors) auto-hide on empty
// list inputs — the empty default renders nothing. "Load sample data"
// pulls the first matching instance from the kitchen-sink fixture so the
// preview shows realistic populated content with one click. Banks's
// gallery walkthrough relies on this.

import { useMemo, useState } from 'preact/hooks'

import kitchenSinkFixture from '../../../tests/fixtures/kitchen-sink.json'
import { blocks, type BlockId } from '../../registry/blocks'
import { secondaryButtonClass } from '../button-classes'
import type { Email } from '../../store/schemas'
import type { Viewport } from '../../types'
import { Form } from '../form/form'
import { useRoute } from '../hooks/use-route'
import { PreviewIframe } from '../preview/preview-iframe'
import { PreviewToolbar } from '../preview/preview-toolbar'

interface KitchenSinkBlock {
  readonly instanceId: string
  readonly blockId: string
  readonly options: unknown
  readonly inputs: unknown
}

function findSampleForBlock(blockId: string): { options: unknown; inputs: unknown } | null {
  const ks = kitchenSinkFixture as { blocks: readonly KitchenSinkBlock[] }
  const sample = ks.blocks.find((b) => b.blockId === blockId)
  /* v8 ignore next -- kitchen-sink fixture covers every registered Block; missing-sample arm is defensive */
  return sample === undefined ? null : { options: sample.options, inputs: sample.inputs }
}

const PREVIEW_TIMESTAMP = '2026-01-01T00:00:00.000Z'

function buildSyntheticEmail(
  blockId: string,
  options: unknown,
  inputs: unknown,
  emailName: string,
): Email {
  return {
    id: 'block-gallery-preview',
    name: emailName,
    templateId: '',
    templateName: '',
    createdAt: PREVIEW_TIMESTAMP,
    updatedAt: PREVIEW_TIMESTAMP,
    schemaVersion: 1,
    subjectLine: '',
    blocks: [
      {
        instanceId: 'preview-instance',
        blockId,
        options,
        inputs,
        included: true,
      },
    ],
  }
}

function BlockNotFound({ id }: { id: string }) {
  return (
    <div class="space-y-4">
      <h2 class="m-0 text-lg font-semibold">Block not found</h2>
      <p class="m-0 text-sm text-gray-600">
        No registered Block with id <code class="font-mono">{id}</code>.
      </p>
      <a href="#/blocks" class={`inline-block ${secondaryButtonClass}`}>
        ← Back to Blocks
      </a>
    </div>
  )
}

export function BlockPreviewScreen() {
  const { params } = useRoute()
  /* v8 ignore next -- router gates `block-preview` on a non-empty `:id` capture; fallback is defensive */
  const id = params.id ?? ''
  const def = blocks[id as BlockId] as (typeof blocks)[BlockId] | undefined
  if (def === undefined) return <BlockNotFound id={id} />
  // `key={id}` forces a fresh mount of <BlockPreviewBody> on every blockId
  // navigation, so the per-block options/inputs state initializes from the
  // new block's `defaultOptions`/`defaultInputs` rather than carrying over
  // the previous block's (mismatching) shape.
  return <BlockPreviewBody key={id} id={id} def={def} />
}

interface BlockPreviewBodyProps {
  readonly id: string
  readonly def: (typeof blocks)[BlockId]
}

function BlockPreviewBody({ id, def }: BlockPreviewBodyProps) {
  const sample = useMemo(() => findSampleForBlock(id), [id])
  const [options, setOptions] = useState<unknown>(() => def.defaultOptions)
  const [inputs, setInputs] = useState<unknown>(() => def.defaultInputs)
  const [viewport, setViewport] = useState<Viewport>('desktop')

  const syntheticEmail = useMemo(
    () => buildSyntheticEmail(id, options, inputs, `${def.label} preview`),
    [def, id, options, inputs],
  )

  return (
    <div class="space-y-4">
      <header class="flex items-baseline justify-between gap-4">
        <div class="min-w-0">
          <div class="text-xs">
            <a href="#/blocks" class="text-blue-700 hover:underline">
              ← Back to Blocks
            </a>
          </div>
          <h2 class="m-0 mt-1 flex items-baseline gap-2 truncate text-lg font-semibold">
            <span class="truncate">{def.label}</span>
            <code class="text-xs font-normal text-gray-500">{def.id}</code>
          </h2>
          <div class="text-xs text-gray-500">Category: {def.category}</div>
        </div>
        <div class="flex shrink-0 gap-2">
          {sample !== null && (
            <button
              type="button"
              class={secondaryButtonClass}
              onClick={() => {
                setOptions(sample.options)
                setInputs(sample.inputs)
              }}
            >
              Load sample data
            </button>
          )}
          <button
            type="button"
            class={secondaryButtonClass}
            onClick={() => {
              setOptions(def.defaultOptions)
              setInputs(def.defaultInputs)
            }}
          >
            Reset to defaults
          </button>
        </div>
      </header>

      {/*
       * Stacked vertical layout: preview on top, form below. The page
       * container's max-w-screen-lg (1024 px) gives the preview pane a
       * full content width — comfortably more than the email's 680 px
       * iframe, so the right portion (where per-style header text lives)
       * is never clipped. Side-by-side editing wasn't worth the iframe
       * truncation cost on Banks's gallery walkthrough.
       */}
      <section aria-label="Preview pane">
        <PreviewToolbar viewport={viewport} onViewportChange={setViewport} />
        <div class="overflow-auto bg-gray-100 p-2">
          <PreviewIframe
            email={syntheticEmail}
            viewport={viewport}
            className="mx-auto min-h-[600px]"
            isolated
            autoHeight
          />
        </div>
      </section>
      <section aria-label="Edit pane" class="space-y-4">
        <div class="rounded border border-gray-200 bg-white p-3">
          <h3 class="m-0 mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
            Options
          </h3>
          <Form
            schema={def.optionsSchema}
            value={options}
            onChange={setOptions}
            idPrefix="block-preview-options"
            blockId={def.id}
          />
        </div>
        <div class="rounded border border-gray-200 bg-white p-3">
          <h3 class="m-0 mb-2 text-sm font-semibold uppercase tracking-wide text-gray-600">
            Inputs
          </h3>
          <Form
            schema={def.inputsSchema}
            value={inputs}
            onChange={setInputs}
            idPrefix="block-preview-inputs"
            blockId={def.id}
          />
        </div>
      </section>
    </div>
  )
}
